import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { ErrorNotice } from '../../components/ErrorNotice';
import { downloadArticleAttachment } from './api';
import { formatSize, useFileDownload } from '../tickets/articles/attachmentFile';
import {
  useArticleAttachments,
  useRemoveArticleAttachment,
  useUploadArticleAttachment,
} from './queries';

interface ArticleFilesProps {
  articleId: string;
  canWrite: boolean;
}

/**
 * Die Dateien an einem Wissensartikel -- F4-09, K-27.
 *
 * **Aus der Maske herausgeloest.** Vorher standen Anhaenge, Bewertung und
 * Fassungen als drei weitere Ueberschriften unter einem Formular mit neun
 * Feldern -- in einer Spalte, die man dafuer bis zum Ende rollen musste. Hier
 * sind sie ein eigener Reiter: Wer eine Datei sucht, sucht sie nicht unter
 * dem Aenderungsvermerk.
 *
 * **Die Ablegeflaeche ist die Beschriftung des Dateifelds.** Ein Klick
 * darauf oeffnet den Dateiwaehler, ein Zug darueber legt ab -- zwei Wege,
 * dasselbe Ziel. Das Feld selbst bleibt bestehen und wird nur aus dem Bild
 * genommen: Ein versteckter Dateiwaehler waere mit der Tastatur nicht mehr
 * erreichbar, und dann haette der Zugewinn fuer die Maus jemand anderem etwas
 * weggenommen.
 */
export function ArticleFiles({ articleId, canWrite }: ArticleFilesProps) {
  const { t } = useTranslation();
  const attachments = useArticleAttachments(articleId);
  const upload = useUploadArticleAttachment(articleId);
  const remove = useRemoveArticleAttachment(articleId);
  const fetchFile = useFileDownload();
  const [over, setOver] = useState(false);

  const dateien = attachments.data ?? [];

  return (
    <div className="kb-files">
      {upload.isError ? <ErrorNotice error={upload.error} /> : null}
      {remove.isError ? <ErrorNotice error={remove.error} /> : null}
      {fetchFile.isError ? <ErrorNotice error={fetchFile.error} /> : null}

      {dateien.length === 0 ? (
        <p className="table__muted">{t('knowledge.noAttachments')}</p>
      ) : (
        dateien.map((attachment) => (
          <div key={attachment.id} className="kb-file">
            <span className="kb-file__name">
              <button
                type="button"
                className="table__subject"
                onClick={() => {
                  fetchFile.mutate({
                    fetchFile: () => downloadArticleAttachment(articleId, attachment.id),
                    fallbackName: attachment.filename,
                  });
                }}
              >
                {attachment.filename}
              </button>
            </span>
            <span className="kb-file__size">{formatSize(attachment.size_bytes)}</span>
            {/* Ungeprueft heisst ungeprueft -- docs/04, Abschnitt 7. */}
            {attachment.virus_scanned_at === null ? (
              <Badge tone="warning">{t('articles.notScanned')}</Badge>
            ) : null}
            {canWrite ? (
              <Button
                variant="ghost"
                busy={remove.isPending}
                aria-label={t('knowledge.removeAttachmentOf', { name: attachment.filename })}
                onClick={() => {
                  if (!window.confirm(t('knowledge.removeAttachmentConfirm'))) return;
                  remove.mutate(attachment.id);
                }}
              >
                {t('articles.removeAttachment')}
              </Button>
            ) : null}
          </div>
        ))
      )}

      {canWrite ? (
        <>
          <label
            className={over ? 'kb-dropzone kb-dropzone--over' : 'kb-dropzone'}
            onDragOver={(event) => {
              event.preventDefault();
              setOver(true);
            }}
            onDragLeave={() => {
              setOver(false);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setOver(false);
              const file = event.dataTransfer.files[0];
              if (file) upload.mutate(file);
            }}
          >
            <span>{t('knowledge.dropHint')}</span>
            <input
              type="file"
              className="visually-hidden"
              aria-label={t('knowledge.addAttachment')}
              disabled={upload.isPending}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (!file) return;
                upload.mutate(file);
                event.target.value = '';
              }}
            />
          </label>
          <span className="field__hint">{t('knowledge.attachmentsHint')}</span>
        </>
      ) : null}
    </div>
  );
}
